import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router';
import { normalizeUsername } from '../../shared/events';
import { initials } from '../components/Feed';
import { session, useSession, type FeedRow } from '../lib/session';

/**
 * "Killfeed banner" OBS overlay: shows the single newest comment (avatar, name, text) for a few
 * seconds, then fades out to a fully transparent page. Silent, no voice or sounds.
 *
 * URL: /overlay/killfeed?u=<tiktok username>
 *   hold=6           seconds each comment stays up (2 to 30)
 *   scale=1          size multiplier (0.5 to 2); the banner is 500 px wide at 1
 *   accent=e8c36a    rim and name colour, hex without #
 *   name=handle      show the @handle instead of the display name (the default since Sept 29 2026;
 *                    name=nickname is still accepted and means the default)
 *   demo=1           simulated comments, no TikTok connection (for positioning in OBS)
 */

const EMOTE_RE = /\[[a-z0-9_]+\]/gi;
const HEX_GRADIENTS = ['linear-gradient(145deg, #3fa9ff, #1a3f8f)', 'linear-gradient(145deg, #ff8a5c, #a3243a)', 'linear-gradient(145deg, #5be3b0, #1d6b55)', 'linear-gradient(145deg, #c08cff, #4b2a8f)'];

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

interface Card {
  key: string;
  user: FeedRow['user'];
  text: string;
  leaving: boolean;
}

export function Killfeed() {
  const [params] = useSearchParams();
  const username = normalizeUsername(params.get('u'));
  const demo = params.get('demo') === '1';
  const holdMs = clamp(Number(params.get('hold')) || 6, 2, 30) * 1000;
  const scale = clamp(Number(params.get('scale')) || 1, 0.5, 2);
  const accentParam = params.get('accent') ?? '';
  const accent = /^[0-9a-f]{6}$/i.test(accentParam) ? `#${accentParam}` : '#e8c36a';
  const useNickname = params.get('name') !== 'handle';

  const s = useSession();
  const [card, setCard] = useState<Card | null>(null);
  const lastShown = useRef<string | null>(null);
  const timers = useRef<number[]>([]);

  const show = (row: Pick<FeedRow, 'id' | 'user' | 'text'>) => {
    const text = row.text.replace(EMOTE_RE, ' ').replace(/\s+/g, ' ').trim();
    if (!text) return;
    timers.current.forEach((id) => window.clearTimeout(id));
    setCard({ key: row.id, user: row.user, text, leaving: false });
    timers.current = [
      window.setTimeout(() => setCard((c) => (c && c.key === row.id ? { ...c, leaving: true } : c)), holdMs),
      window.setTimeout(() => setCard((c) => (c && c.key === row.id ? null : c)), holdMs + 400),
    ];
  };

  useEffect(() => {
    document.body.classList.add('overlay');
    if (username && !demo) session.start(username, { silent: true, retryForever: true, page: 'killfeed' });
    return () => {
      document.body.classList.remove('overlay');
      timers.current.forEach((id) => window.clearTimeout(id));
      if (!demo) session.stop();
    };
  }, [username, demo]);

  // Newest comment wins: whatever arrived last is shown immediately, even if others were skipped.
  useEffect(() => {
    if (demo) return;
    for (let i = s.feed.length - 1; i >= 0; i--) {
      const row = s.feed[i];
      if (row.kind !== 'chat') continue;
      if (row.id !== lastShown.current) {
        lastShown.current = row.id;
        show(row);
      }
      break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.feed, demo]);

  useEffect(() => {
    if (!demo) return;
    let n = 0;
    let timer = 0;
    const next = () => {
      const d = DEMO[n++ % DEMO.length];
      show({ id: `demo-${n}`, user: { uniqueId: d[0], nickname: d[1] }, text: d[2] });
      timer = window.setTimeout(next, 1200 + Math.random() * 3800);
    };
    timer = window.setTimeout(next, 600);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo]);

  if (!username && !demo) {
    return <div className="kf-hint">Add ?u=tiktok_username to this URL (or ?demo=1 to preview).</div>;
  }

  const style = { '--kf-accent': accent, '--kf-scale': scale } as CSSProperties;
  return (
    <div className="kf" style={style} aria-live="polite">
      {card && (
        <div key={card.key} className={`kf__card${card.leaving ? ' kf__card--out' : ''}`}>
          <div className="kf__hex">
            <div className="kf__hex-inner" style={{ background: HEX_GRADIENTS[hash(card.user.uniqueId || card.user.nickname) % HEX_GRADIENTS.length] }}>
              <Avatar user={card.user} />
            </div>
          </div>
          <div className="kf__plate">
            <div className="kf__name">{useNickname ? card.user.nickname || card.user.uniqueId : `@${card.user.uniqueId || card.user.nickname}`}</div>
            <div className="kf__text">{card.text}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Avatar({ user }: { user: FeedRow['user'] }) {
  const [broken, setBroken] = useState(false);
  if (user.avatar && !broken) {
    return <img src={user.avatar} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
  }
  return <span>{initials(user).slice(0, 2)}</span>;
}

/** Sample chatter for demo=1 (handle, display name, comment). */
const DEMO: Array<[string, string, string]> = [
  ['aimanz', 'Aiman', 'GG bang, savage tadi memang padu!'],
  ['nurul.xx', 'Nurul', 'Main Ling lagi bang'],
  ['dimas_ml', 'Dimas', 'Mantap bang, gas terus'],
  ['sarah.gaming', 'Sarah', 'Bang build Fanny dong, penasaran'],
  ['hafiz_220', 'Hafiz', 'Lord dah spawn, jom push mid'],
  ['rizkyyy', 'Rizky', 'Wkwk epic comeback'],
  ['mei.ling', 'Mei Ling', 'Hello from Penang! First time join live ni'],
  ['kocokpidong34', 'KOCOKPIDONG', 'alo bebiii'],
];
